import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../admin/api";
import { toast } from "sonner";
import { Search, Archive, ArchiveRestore, Trash2, FileDown, X, Mail, Phone } from "lucide-react";

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | open | archived
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (filter === "open") params.set("archived", "false");
      if (filter === "archived") params.set("archived", "true");
      params.set("limit", "200");
      const { data } = await api.get(`/admin/submissions?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [q, filter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleArchive = async (s) => {
    try {
      await api.patch(`/admin/submissions/${s.id}`, { archived: !s.archived });
      toast.success(s.archived ? "Wiederhergestellt." : "Archiviert.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Anfrage von ${s.name} wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/submissions/${s.id}`);
      toast.success("Gelöscht.");
      setFocus(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  return (
    <div data-testid="admin-inquiries" className="p-6 md:p-10 max-w-6xl">
      <div className="font-mono-tech text-[#1d428a] mb-2">
        <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
        ADMIN · KONTAKTANFRAGEN
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#0A0A0A]">Anfragen.</h1>
      <p className="mt-2 text-[#5A6270] text-sm">{total} Einträge insgesamt</p>

      <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center bg-white border border-[#1d428a]/30 flex-1 px-3 py-2">
          <Search size={16} className="text-[#5A6270]" />
          <input
            data-testid="admin-inquiries-search"
            type="text"
            placeholder="Suchen nach Name, E-Mail, Firma, Nachricht…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="ml-2 flex-1 outline-none bg-transparent text-sm"
          />
        </div>

        <div className="flex items-center border border-[#1d428a]">
          {[
            { id: "all", label: "ALLE" },
            { id: "open", label: "OFFEN" },
            { id: "archived", label: "ARCHIV" },
          ].map((f, i) => (
            <button
              key={f.id}
              type="button"
              data-testid={`admin-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`font-mono-tech px-3 py-2 ${
                filter === f.id ? "bg-[#1d428a] text-white" : "text-[#1d428a] hover:bg-[#1d428a]/10"
              } ${i !== 2 ? "border-r border-[#1d428a]" : ""}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions.csv`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-[#1d428a] text-[#1d428a] font-mono-tech px-4 py-2 hover:bg-[#1d428a] hover:text-white transition-colors"
        >
          <FileDown size={14} />
          CSV
        </a>
      </div>

      <div className="mt-6 border border-[#1d428a]/15 bg-white">
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 font-mono-tech text-[#5A6270] border-b border-[#1d428a]/15 bg-[#e8ecf3]/40">
          <div className="col-span-2">DATUM</div>
          <div className="col-span-2">NAME</div>
          <div className="col-span-3">E-MAIL</div>
          <div className="col-span-2">EINSATZ</div>
          <div className="col-span-1">SPR</div>
          <div className="col-span-2 text-right">AKTIONEN</div>
        </div>

        {loading && <div className="p-6 text-[#5A6270] text-sm">Lädt…</div>}

        {!loading && items.length === 0 && (
          <div className="p-6 text-[#5A6270] text-sm">Keine Anfragen für diese Filter.</div>
        )}

        <div className="divide-y divide-[#1d428a]/10">
          {items.map((s) => (
            <div
              key={s.id}
              className={`grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-[#e8ecf3]/60 transition-colors ${
                s.archived ? "opacity-60" : ""
              }`}
            >
              <button
                type="button"
                onClick={() => setFocus(s)}
                className="col-span-12 md:col-span-2 text-left font-mono-tech text-[#5A6270]"
              >
                {new Date(s.created_at).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" })}
              </button>
              <button
                type="button"
                onClick={() => setFocus(s)}
                className="col-span-12 md:col-span-2 text-left font-display-mid text-[#0A0A0A] truncate"
              >
                {s.name}
              </button>
              <button
                type="button"
                onClick={() => setFocus(s)}
                className="col-span-12 md:col-span-3 text-left text-sm text-[#5A6270] truncate"
              >
                {s.email}
              </button>
              <div className="col-span-12 md:col-span-2 font-mono-tech text-[#1d428a]">{s.project_type}</div>
              <div className="col-span-12 md:col-span-1 font-mono-tech text-[#df4661] uppercase">{s.language}</div>
              <div className="col-span-12 md:col-span-2 flex md:justify-end gap-1">
                <button
                  type="button"
                  data-testid={`admin-toggle-archive-${s.id}`}
                  onClick={() => toggleArchive(s)}
                  className="p-2 hover:bg-[#1d428a] hover:text-white transition-colors text-[#1d428a]"
                  title={s.archived ? "Wiederherstellen" : "Archivieren"}
                >
                  {s.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(s)}
                  className="p-2 hover:bg-[#df4661] hover:text-white transition-colors text-[#df4661]"
                  title="Löschen"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {focus && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
          onClick={() => setFocus(null)}
          data-testid="admin-inquiry-detail"
        >
          <div
            className="bg-white w-full max-w-2xl border border-[#1d428a] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-[#1d428a]/15 bg-[#1d428a] text-white">
              <div>
                <div className="font-mono-tech text-white/70">
                  {new Date(focus.created_at).toLocaleString("de-CH")}
                </div>
                <div className="font-display text-2xl mt-1">{focus.name}</div>
              </div>
              <button onClick={() => setFocus(null)} className="text-white/80 hover:text-white" type="button">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-wrap gap-3">
                <a
                  href={`mailto:${focus.email}`}
                  className="inline-flex items-center gap-2 bg-[#1d428a] text-white font-mono-tech px-4 py-2.5 hover:bg-[#df4661] transition-colors"
                >
                  <Mail size={14} /> {focus.email}
                </a>
                {focus.phone && (
                  <a
                    href={`tel:${focus.phone.replace(/\s+/g, "")}`}
                    className="inline-flex items-center gap-2 border border-[#1d428a] text-[#1d428a] font-mono-tech px-4 py-2.5 hover:bg-[#1d428a] hover:text-white transition-colors"
                  >
                    <Phone size={14} /> {focus.phone}
                  </a>
                )}
              </div>

              <Detail label="Firma" value={focus.company || "—"} />
              <Detail label="Einsatzart" value={focus.project_type} />
              <Detail label="Sprache" value={focus.language?.toUpperCase()} />
              <div>
                <div className="font-mono-tech text-[#df4661] mb-2">NACHRICHT</div>
                <div className="bg-[#fafbfd] border-l-2 border-[#df4661] p-4 text-sm leading-relaxed whitespace-pre-line">
                  {focus.message}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t border-[#1d428a]/15">
                <button
                  type="button"
                  onClick={() => toggleArchive(focus).then(() => setFocus({ ...focus, archived: !focus.archived }))}
                  className="inline-flex items-center gap-2 border border-[#1d428a] text-[#1d428a] font-mono-tech px-4 py-2 hover:bg-[#1d428a] hover:text-white transition-colors"
                >
                  {focus.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                  {focus.archived ? "WIEDERHERSTELLEN" : "ARCHIVIEREN"}
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(focus)}
                  className="inline-flex items-center gap-2 bg-[#df4661] text-white font-mono-tech px-4 py-2 hover:bg-[#1d428a] transition-colors"
                >
                  <Trash2 size={14} /> LÖSCHEN
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div className="grid grid-cols-12 gap-3 border-b border-[#1d428a]/10 pb-2">
    <div className="col-span-4 font-mono-tech text-[#5A6270]">{label.toUpperCase()}</div>
    <div className="col-span-8 text-sm text-[#0A0A0A]">{value}</div>
  </div>
);
